import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  radiosConProgramacion,
  programas,
  eventosAutomaticos,
  tvAppData,
  statsProgramacionAutomatica,
  reglasProgramacionAutomatica,
  categoriaMeta,
  vistaOyenteMeta,
  resumenGlobal,
  getProgramasPorDia,
  type VistaOyente,
  type RadioConProgramacion,
  type ProgramaCompleto,
  type EventoAutomatico,
} from '@/mocks/programacionAutomatica';

export default function ProgramacionAutomatica() {
  const [radioId, setRadioId] = useState<number>(1);
  const [vistaActiva, setVistaActiva] = useState<VistaOyente>('ahora');
  const [mostrarEventos, setMostrarEventos] = useState(false);
  const [mostrarTVApp, setMostrarTVApp] = useState(false);
  const [modalEvento, setModalEvento] = useState<EventoAutomatico | null>(null);

  const radio = radiosConProgramacion.find((r) => r.id === radioId) ?? radiosConProgramacion[0];
  const programasDeRadio = programas.filter((p) => p.radioId === radioId);
  const tv = tvAppData.find((t) => t.radioFrecuencia === radio.frecuencia);
  const eventosRadio = eventosAutomaticos.filter((e) => e.radioId === radioId);

  const ahora = radio.programaActual;
  const siguiente = radio.programaSiguiente;
  const masTarde = radio.programasHoy.filter((p) => p !== ahora && p !== siguiente);
  if (siguiente && !masTarde.includes(siguiente)) masTarde.unshift(siguiente);

  const diasSemana: ('Lunes' | 'Martes' | 'Miércoles' | 'Jueves' | 'Viernes' | 'Sábado' | 'Domingo')[] = [
    'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo',
  ];

  const vistas = [
    { id: 'ahora' as const, label: 'Ahora', icono: 'ri-play-circle-line' },
    { id: 'siguiente' as const, label: 'Siguiente', icono: 'ri-skip-forward-line' },
    { id: 'mas-tarde' as const, label: 'Más Tarde', icono: 'ri-time-line' },
    { id: 'semana' as const, label: 'Semana', icono: 'ri-calendar-line' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      {/* Top Bar */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2 text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              <span className="text-sm font-medium hidden sm:inline">RFW</span>
            </Link>
            <div className="w-px h-5 bg-background-200"></div>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg overflow-hidden flex-shrink-0">
                <img src={radio.logo} alt={radio.nombre} className="w-full h-full object-cover" />
              </div>
              <div className="text-sm">
                <span className="font-semibold text-foreground-950">{radio.nombre}</span>
                <span className="text-foreground-400 ml-1.5">{radio.frecuencia} FM</span>
              </div>
              {radio.enVivo && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  EN VIVO
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setMostrarEventos(!mostrarEventos)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                mostrarEventos ? 'bg-amber-100 text-amber-700' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}
            >
              <i className="ri-flashlight-line"></i>
              <span className="hidden sm:inline">Eventos Auto</span>
              <span className="w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] flex items-center justify-center font-bold">{eventosRadio.length}</span>
            </button>
            <button
              onClick={() => setMostrarTVApp(!mostrarTVApp)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                mostrarTVApp ? 'bg-violet-100 text-violet-700' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}
            >
              <i className="ri-tv-2-line"></i>
              <span className="hidden sm:inline">TV App</span>
            </button>
          </div>
        </div>

        {/* Radio quick-select bar */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 pb-3 flex items-center gap-2 overflow-x-auto">
          <span className="text-[10px] font-medium text-foreground-400 uppercase tracking-wider flex-shrink-0">Radios:</span>
          {radiosConProgramacion.map((r) => (
            <button
              key={r.id}
              onClick={() => setRadioId(r.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all cursor-pointer flex-shrink-0 ${
                radioId === r.id
                  ? 'bg-rose-500 text-white'
                  : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${r.enVivo ? 'bg-emerald-400 animate-pulse' : 'bg-foreground-300'}`}></span>
              {r.frecuencia}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* Radio hero */}
            <div className="bg-gradient-to-br from-rose-50 to-amber-50 border border-rose-100/60 rounded-2xl p-5 md:p-6 mb-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-16 h-16 rounded-2xl overflow-hidden flex-shrink-0 shadow-sm">
                  <img src={radio.logo} alt={radio.nombre} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-xl font-bold text-foreground-950">{radio.nombre}</h1>
                    <span className="px-2 py-0.5 rounded-md bg-rose-100 text-rose-700 text-xs font-semibold">{radio.frecuencia} FM</span>
                    <span className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 text-xs font-medium">{radio.categoria}</span>
                  </div>
                  <p className="text-sm text-foreground-500 mt-1">
                    {radio.ciudad}, {radio.pais} · {radio.oyentesActuales.toLocaleString()} oyentes conectados · {programasDeRadio.length} programas activos
                  </p>
                </div>
                {ahora && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-xl flex-shrink-0">
                    <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
                    <span className="text-xs font-semibold whitespace-nowrap">Al aire: {ahora.nombre}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Vista tabs */}
            <div className="flex gap-1 mb-6 p-1 bg-background-100 rounded-full overflow-x-auto">
              {vistas.map((v) => (
                <button
                  key={v.id}
                  onClick={() => setVistaActiva(v.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    vistaActiva === v.id
                      ? 'bg-white text-foreground-950 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  <i className={v.icono}></i>
                  {v.label}
                </button>
              ))}
            </div>

            {/* Vista: Ahora */}
            {vistaActiva === 'ahora' && (
              <VistaAhora ahora={ahora} siguiente={siguiente} radio={radio} />
            )}

            {/* Vista: Siguiente */}
            {vistaActiva === 'siguiente' && (
              <VistaSiguiente siguiente={siguiente} ahora={ahora} />
            )}

            {/* Vista: Más Tarde */}
            {vistaActiva === 'mas-tarde' && (
              <VistaMasTarde programas={masTarde.filter((p) => p !== ahora)} />
            )}

            {/* Vista: Semana */}
            {vistaActiva === 'semana' && (
              <VistaSemana radioId={radioId} diasSemana={diasSemana} />
            )}

            {/* Eventos automáticos panel */}
            {mostrarEventos && (
              <div className="mt-6">
                <PanelEventosAutomaticos
                  eventos={eventosRadio}
                  onVerDetalle={setModalEvento}
                />
              </div>
            )}

            {/* TV App panel */}
            {mostrarTVApp && tv && (
              <div className="mt-6">
                <PanelTVApp tv={tv} radio={radio} />
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="w-full lg:w-72 flex-shrink-0">
            <SidebarInfo
              radio={radio}
              stats={statsProgramacionAutomatica}
              reglas={reglasProgramacionAutomatica}
              resumen={resumenGlobal}
            />
          </aside>
        </div>
      </main>

      {/* Modal detalle evento */}
      {modalEvento && (
        <ModalEvento evento={modalEvento} onClose={() => setModalEvento(null)} />
      )}
    </div>
  );
}

/* ─── VISTA: AHORA ─── */
function VistaAhora({ ahora, siguiente, radio }: { ahora: ProgramaCompleto | null; siguiente: ProgramaCompleto | null; radio: RadioConProgramacion }) {
  if (!ahora) {
    return (
      <div className="bg-background-50 border border-background-200/70 rounded-2xl p-10 text-center">
        <div className="w-20 h-20 mx-auto mb-5 rounded-2xl bg-background-100 flex items-center justify-center">
          <i className="ri-headphone-line text-4xl text-foreground-300"></i>
        </div>
        <h3 className="text-lg font-bold text-foreground-900 mb-2">Silencio al aire</h3>
        <p className="text-sm text-foreground-500 max-w-md mx-auto mb-2">
          No hay ningún programa transmitiendo en {radio.nombre} en este momento.
        </p>
        {siguiente && (
          <div className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-sky-50 border border-sky-200/60 rounded-xl">
            <i className="ri-skip-forward-line text-sky-500"></i>
            <span className="text-sm text-sky-700">
              <strong>{siguiente.nombre}</strong> comienza a las {siguiente.horarios[0].inicio}
            </span>
          </div>
        )}
      </div>
    );
  }

  const cat = categoriaMeta[ahora.categoria];
  const locutor = ahora.locutores[0];
  const horario = ahora.horarios[0];

  return (
    <div className="space-y-6">
      {/* Main now playing card */}
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200/50 rounded-2xl overflow-hidden">
        <div className="relative h-48 md:h-56">
          <img src={ahora.portada} alt={ahora.nombre} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/80 via-emerald-900/30 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse"></span>
              <span className="text-xs font-bold text-white uppercase tracking-widest">En Vivo Ahora</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-1">{ahora.nombre}</h2>
            <div className="flex items-center gap-3 text-white/80 text-sm">
              <span className="flex items-center gap-1"><i className="ri-user-voice-line"></i>{locutor.nombre}</span>
              <span>{horario.inicio} - {horario.fin}</span>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Locutor info */}
          <div className="flex items-start gap-4 mb-5 p-4 bg-white/60 rounded-xl">
            <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 border-2 border-white">
              <img src={locutor.avatar} alt={locutor.nombre} className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-bold text-foreground-950">{locutor.nombre}</h4>
                <span className="px-1.5 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[10px] font-semibold">AL AIRE</span>
              </div>
              <p className="text-xs text-foreground-500 mt-0.5">{locutor.rol}</p>
              <p className="text-xs text-foreground-400 mt-1 line-clamp-2">{locutor.bio}</p>
            </div>
          </div>

          {/* Descripción */}
          <p className="text-sm text-foreground-600 leading-relaxed mb-4">{ahora.descripcion}</p>

          {/* Categoría y tags */}
          <div className="flex flex-wrap items-center gap-2 mb-5">
            <span className={`px-2.5 py-1 rounded-md ${cat.bgColor} ${cat.textColor} text-xs font-medium`}>
              <i className={`${cat.icono} mr-1`}></i>{ahora.categoria}
            </span>
            <span className="px-2 py-1 bg-white/60 rounded-md text-xs text-foreground-500">{ahora.duracion}</span>
            {ahora.tags.slice(0, 4).map((tag) => (
              <span key={tag} className="px-2 py-1 bg-white/40 rounded-md text-[11px] text-foreground-400">{tag}</span>
            ))}
          </div>

          {/* Auto-activation status */}
          <div className="border-t border-emerald-200/50 pt-4">
            <h4 className="text-xs font-semibold text-foreground-700 mb-3 flex items-center gap-2">
              <i className="ri-flashlight-line text-amber-500"></i>
              Activaciones automáticas completadas
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {[
                { label: 'Programa Activo', icono: 'ri-play-circle-line', ok: true },
                { label: 'Locutor Al Aire', icono: 'ri-user-voice-line', ok: true },
                { label: 'Notificaciones', icono: 'ri-notification-3-line', ok: true },
                { label: 'Cabina Actualizada', icono: 'ri-message-3-line', ok: true },
                { label: 'TV App Refresh', icono: 'ri-tv-2-line', ok: true },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2 p-2 bg-white/50 rounded-lg text-xs">
                  <span className="w-4 h-4 rounded-full bg-emerald-400 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-white text-[8px]"></i>
                  </span>
                  <span className="text-foreground-600 whitespace-nowrap">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Próximo programa */}
      {siguiente && (
        <div className="bg-background-50 border border-background-200/70 rounded-2xl p-5 flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
            <img src={siguiente.portada} alt={siguiente.nombre} className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-foreground-400 uppercase tracking-wide">A continuación</p>
            <h4 className="text-sm font-bold text-foreground-950">{siguiente.nombre}</h4>
            <p className="text-xs text-foreground-500">
              <i className="ri-time-line mr-1"></i>{siguiente.horarios[0].inicio} · {siguiente.locutores[0].nombre}
            </p>
          </div>
          <div className="flex-shrink-0">
            <span className="px-3 py-1.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-lg whitespace-nowrap">
              {siguiente.horarios[0].inicio}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── VISTA: SIGUIENTE ─── */
function VistaSiguiente({ siguiente, ahora }: { siguiente: ProgramaCompleto | null; ahora: ProgramaCompleto | null }) {
  if (!siguiente) {
    return (
      <div className="bg-background-50 border border-background-200/70 rounded-2xl p-10 text-center">
        <div className="w-20 h-20 mx-auto mb-5 rounded-2xl bg-background-100 flex items-center justify-center">
          <i className="ri-skip-forward-line text-4xl text-foreground-300"></i>
        </div>
        <h3 className="text-lg font-bold text-foreground-900 mb-2">Fin de la programación</h3>
        <p className="text-sm text-foreground-500">No hay más programas programados para hoy. La programación se reanuda mañana.</p>
      </div>
    );
  }

  const cat = categoriaMeta[siguiente.categoria];
  const locutor = siguiente.locutores[0];
  const horario = siguiente.horarios[0];

  return (
    <div className="bg-gradient-to-br from-sky-50 to-cyan-50 border border-sky-200/50 rounded-2xl overflow-hidden">
      <div className="relative h-44 md:h-52">
        <img src={siguiente.portada} alt={siguiente.nombre} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-sky-900/80 via-sky-900/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-sky-500/90 text-white text-xs font-semibold mb-2">
            <i className="ri-skip-forward-line"></i>
            PRÓXIMO PROGRAMA
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-1">{siguiente.nombre}</h2>
          <div className="flex items-center gap-3 text-white/80 text-sm">
            <span className="flex items-center gap-1"><i className="ri-user-voice-line"></i>{locutor.nombre}</span>
            <span className="flex items-center gap-1 font-semibold"><i className="ri-time-line"></i>Empieza a las {horario.inicio}</span>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-start gap-4 mb-5 p-4 bg-white/60 rounded-xl">
          <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 border-2 border-white">
            <img src={locutor.avatar} alt={locutor.nombre} className="w-full h-full object-cover" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-foreground-950">{locutor.nombre}</h4>
            <p className="text-xs text-foreground-500 mt-0.5">{locutor.rol}</p>
            <p className="text-xs text-foreground-400 mt-1 line-clamp-2">{locutor.bio}</p>
          </div>
        </div>

        <p className="text-sm text-foreground-600 leading-relaxed mb-4">{siguiente.descripcion}</p>

        <div className="flex flex-wrap items-center gap-2 mb-5">
          <span className={`px-2.5 py-1 rounded-md ${cat.bgColor} ${cat.textColor} text-xs font-medium`}>
            <i className={`${cat.icono} mr-1`}></i>{siguiente.categoria}
          </span>
          <span className="px-2 py-1 bg-white/60 rounded-md text-xs text-foreground-500">{siguiente.duracion}</span>
        </div>

        {/* Auto-activation preview */}
        <div className="border-t border-sky-200/50 pt-4">
          <h4 className="text-xs font-semibold text-foreground-700 mb-3 flex items-center gap-2">
            <i className="ri-timer-flash-line text-sky-500"></i>
            Se activará automáticamente a las {horario.inicio}
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {[
              { label: 'Programa Activo', icono: 'ri-play-circle-line' },
              { label: 'Locutor Al Aire', icono: 'ri-user-voice-line' },
              { label: 'Notificaciones', icono: 'ri-notification-3-line' },
              { label: 'Cabina Actualizada', icono: 'ri-message-3-line' },
              { label: 'TV App Refresh', icono: 'ri-tv-2-line' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2 p-2 bg-white/50 rounded-lg text-xs">
                <span className="w-4 h-4 rounded-full bg-sky-300 flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-white text-[8px]"></i>
                </span>
                <span className="text-foreground-500 whitespace-nowrap">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── VISTA: MÁS TARDE ─── */
function VistaMasTarde({ programas }: { programas: ProgramaCompleto[] }) {
  if (programas.length === 0) {
    return (
      <div className="bg-background-50 border border-background-200/70 rounded-2xl p-10 text-center">
        <div className="w-20 h-20 mx-auto mb-5 rounded-2xl bg-background-100 flex items-center justify-center">
          <i className="ri-time-line text-4xl text-foreground-300"></i>
        </div>
        <h3 className="text-lg font-bold text-foreground-900 mb-2">Programación completada por hoy</h3>
        <p className="text-sm text-foreground-500">No hay más programas para el resto del día.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-bold text-foreground-900 mb-1 flex items-center gap-2">
        <i className="ri-time-line text-amber-500"></i>
        Lo que viene hoy
      </h3>
      {programas.map((prog) => {
        const cat = categoriaMeta[prog.categoria];
        const locutor = prog.locutores[0];
        const horario = prog.horarios[0];
        return (
          <div key={prog.id} className="bg-background-50 border border-background-200/70 rounded-xl p-4 flex gap-4 hover:border-background-300/60 transition-colors">
            <div className="w-20 h-16 rounded-lg overflow-hidden flex-shrink-0">
              <img src={prog.portada} alt={prog.nombre} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="text-sm font-bold text-foreground-950">{prog.nombre}</h4>
                <span className={`px-1.5 py-0.5 rounded-md ${cat.bgColor} ${cat.textColor} text-[10px] font-medium`}>{prog.categoria}</span>
              </div>
              <p className="text-xs text-foreground-500 mt-0.5 line-clamp-2">{prog.descripcion.slice(0, 100)}</p>
              <div className="flex items-center gap-3 mt-2 text-[11px] text-foreground-400">
                <span className="flex items-center gap-1"><i className="ri-user-voice-line"></i>{locutor.nombre}</span>
                <span className="flex items-center gap-1"><i className="ri-timer-line"></i>{prog.duracion}</span>
                <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{horario.dia}</span>
              </div>
            </div>
            <div className="flex-shrink-0 flex flex-col items-end justify-center gap-1">
              <span className="text-sm font-bold text-foreground-900">{horario.inicio}</span>
              <span className="text-[10px] text-foreground-400">{horario.fin}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── VISTA: SEMANA ─── */
function VistaSemana({ radioId, diasSemana }: { radioId: number; diasSemana: string[] }) {
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-bold text-foreground-900 mb-1 flex items-center gap-2">
        <i className="ri-calendar-line text-violet-500"></i>
        Programación semanal completa
      </h3>
      {diasSemana.map((dia) => {
        const progs = getProgramasPorDia(radioId, dia as any);
        if (progs.length === 0) return null;
        return (
          <div key={dia} className="bg-background-50 border border-background-200/70 rounded-xl p-4">
            <h4 className="text-xs font-semibold text-foreground-700 mb-3 flex items-center gap-2 uppercase tracking-wide">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-400"></span>
              {dia}
            </h4>
            <div className="space-y-2">
              {progs.map((prog) => {
                const cat = categoriaMeta[prog.categoria];
                const horario = prog.horarios.find((h) => h.dia === dia);
                return (
                  <div key={`${prog.id}-${dia}`} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-background-100 transition-colors group">
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={prog.portada} alt={prog.nombre} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-foreground-900 truncate group-hover:text-rose-600 transition-colors">{prog.nombre}</span>
                        <span className={`px-1 py-0.5 rounded ${cat.bgColor} ${cat.textColor} text-[9px] font-medium whitespace-nowrap`}>
                          {prog.categoria}
                        </span>
                      </div>
                      <span className="text-[11px] text-foreground-400">{prog.locutores[0].nombre}</span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className="w-6 h-6 rounded-full bg-rose-50 flex items-center justify-center flex-shrink-0">
                        <i className="ri-play-circle-line text-rose-400 text-xs"></i>
                      </span>
                      <span className="text-xs font-mono font-semibold text-foreground-600">{horario?.inicio}</span>
                      <span className="text-[10px] text-foreground-300">-</span>
                      <span className="text-xs text-foreground-400">{horario?.fin}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── PANEL EVENTOS AUTOMÁTICOS ─── */
function PanelEventosAutomaticos({ eventos, onVerDetalle }: { eventos: EventoAutomatico[]; onVerDetalle: (e: EventoAutomatico) => void }) {
  const colorMap: Record<string, { bg: string; text: string; border: string }> = {
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200/60' },
    sky: { bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200/60' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200/60' },
    violet: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200/60' },
    rose: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200/60' },
    orange: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200/60' },
  };

  return (
    <div className="bg-background-50 border border-amber-200/60 rounded-2xl overflow-hidden">
      <div className="p-4 bg-amber-50 border-b border-amber-200/60 flex items-center gap-2">
        <i className="ri-flashlight-line text-amber-500"></i>
        <h3 className="text-sm font-bold text-amber-800">Registro de Eventos Automáticos</h3>
        <span className="ml-auto text-xs text-amber-600 font-medium">{eventos.length} eventos</span>
      </div>
      <div className="divide-y divide-background-200/60 max-h-80 overflow-y-auto">
        {eventos.map((e) => {
          const c = colorMap[e.color] ?? colorMap.emerald;
          const prog = programas.find((p) => p.id === e.programaId);
          return (
            <div
              key={e.id}
              onClick={() => onVerDetalle(e)}
              className="flex items-start gap-3 p-3 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <div className={`w-8 h-8 rounded-lg ${c.bg} flex items-center justify-center flex-shrink-0`}>
                <i className={`${e.icono} ${c.text} text-sm`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-foreground-900">{e.accion}</span>
                  <span className={`px-1.5 py-0.5 rounded ${c.bg} ${c.text} text-[10px] font-medium`}>
                    {e.tipo.replace(/_/g, ' ')}
                  </span>
                </div>
                <p className="text-[11px] text-foreground-500 mt-0.5 line-clamp-2">{e.detalle}</p>
                {prog && (
                  <p className="text-[10px] text-foreground-400 mt-0.5">{prog.nombre} · {e.horaDisparo.slice(11, 16)}</p>
                )}
              </div>
              <i className="ri-arrow-right-s-line text-foreground-300 flex-shrink-0 mt-1"></i>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── PANEL TV APP ─── */
function PanelTVApp({ tv, radio }: { tv: any; radio: RadioConProgramacion }) {
  return (
    <div className="bg-gradient-to-br from-foreground-900 to-foreground-800 rounded-2xl overflow-hidden text-white">
      <div className="p-4 bg-white/5 border-b border-white/5 flex items-center gap-2">
        <i className="ri-tv-2-line text-violet-400"></i>
        <h3 className="text-sm font-bold">TV App Preview</h3>
        <div className="flex items-center gap-1.5 ml-auto">
          <span className="w-2 h-2 rounded-full bg-red-400"></span>
          <span className="text-[10px] text-white/50">REC</span>
        </div>
      </div>
      <div className="p-5">
        {/* En Vivo Ahora */}
        {tv.enVivoAhora ? (
          <div className="mb-5 p-4 bg-white/10 rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">En Vivo Ahora</span>
            </div>
            <div className="flex gap-4">
              <div className="w-28 h-20 rounded-lg overflow-hidden flex-shrink-0">
                <img src={tv.enVivoAhora.portada} alt={tv.enVivoAhora.nombre} className="w-full h-full object-cover" />
              </div>
              <div>
                <h4 className="text-base font-bold">{tv.enVivoAhora.nombre}</h4>
                <p className="text-xs text-white/60 mt-0.5">{tv.enVivoAhora.locutores[0].nombre}</p>
                <p className="text-xs text-white/40 mt-1">{tv.enVivoAhora.horarios[0].inicio} - {tv.enVivoAhora.horarios[0].fin}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-5 p-4 bg-white/5 rounded-xl text-center text-sm text-white/40">
            Sin transmisión en vivo ahora
          </div>
        )}

        {/* Próximo */}
        {tv.proximoPrograma && (
          <div className="mb-5 p-3 bg-white/5 rounded-xl flex gap-3">
            <div className="w-16 h-12 rounded-lg overflow-hidden flex-shrink-0">
              <img src={tv.proximoPrograma.portada} alt={tv.proximoPrograma.nombre} className="w-full h-full object-cover" />
            </div>
            <div>
              <span className="text-[10px] text-white/40 uppercase tracking-wider">Próximo</span>
              <h5 className="text-xs font-semibold">{tv.proximoPrograma.nombre}</h5>
              <span className="text-[11px] text-white/50">{tv.proximoPrograma.horarios[0].inicio} · {tv.proximoPrograma.locutores[0].nombre}</span>
            </div>
          </div>
        )}

        {/* Programación completa */}
        <div>
          <h5 className="text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-2">Programación Completa</h5>
          <div className="space-y-1">
            {tv.programacionCompleta.slice(0, 6).map((prog: any, i: number) => (
              <div key={i} className="flex items-center gap-2 text-[11px] py-1">
                <span className="text-white/35 font-mono w-12">{prog.horarios[0].inicio}</span>
                <span className="text-white/70 truncate">{prog.nombre}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── SIDEBAR ─── */
function SidebarInfo({ radio, stats, reglas, resumen }: { radio: RadioConProgramacion; stats: any; reglas: any[]; resumen: any }) {
  return (
    <div className="space-y-4">
      {/* Radio profile mini */}
      <div className="bg-background-50 border border-background-200/70 rounded-2xl p-5">
        <div className="text-center mb-4">
          <div className="w-16 h-16 mx-auto rounded-2xl overflow-hidden mb-3">
            <img src={radio.logo} alt={radio.nombre} className="w-full h-full object-cover" />
          </div>
          <h4 className="text-sm font-bold text-foreground-950">{radio.nombre}</h4>
          <p className="text-xs text-foreground-500">{radio.frecuencia} FM · {radio.ciudad}</p>
        </div>
        <div className="grid grid-cols-2 gap-2 text-center">
          <div className="bg-background-100 rounded-lg p-2">
            <div className="text-sm font-bold text-foreground-900">{radio.oyentesActuales.toLocaleString()}</div>
            <div className="text-[10px] text-foreground-400">Oyentes</div>
          </div>
          <div className="bg-background-100 rounded-lg p-2">
            <div className="text-sm font-bold text-foreground-900">{radio.programasSemana.length}</div>
            <div className="text-[10px] text-foreground-400">Programas</div>
          </div>
        </div>
      </div>

      {/* Global stats */}
      <div className="bg-background-50 border border-background-200/70 rounded-2xl p-5">
        <h4 className="text-xs font-semibold text-foreground-700 uppercase tracking-wide mb-3">Estadísticas Globales</h4>
        <div className="space-y-2.5">
          {[
            { label: 'Precisión Horaria', value: stats.precisionHoraria, icono: 'ri-timer-flash-line' },
            { label: 'Tiempo Promedio Activación', value: stats.tiempoPromedioActivacion, icono: 'ri-speed-line' },
            { label: 'Notificaciones Hoy', value: stats.notificacionesEnviadasHoy.toLocaleString(), icono: 'ri-notification-3-line' },
            { label: 'Programas a Tiempo', value: stats.programasIniciadosATiempo.toLocaleString(), icono: 'ri-check-double-line' },
          ].map((s) => (
            <div key={s.label} className="flex items-center justify-between">
              <span className="text-xs text-foreground-500 flex items-center gap-1.5">
                <i className={`${s.icono} text-foreground-300`}></i>
                {s.label}
              </span>
              <span className="text-xs font-semibold text-foreground-900">{s.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Cobertura */}
      <div className="bg-background-50 border border-background-200/70 rounded-2xl p-5">
        <h4 className="text-xs font-semibold text-foreground-700 uppercase tracking-wide mb-3">Cobertura</h4>
        <div className="space-y-2 text-xs text-foreground-500">
          <p className="flex items-center gap-1.5"><i className="ri-earth-line text-foreground-300"></i>{resumen.paises.length} países</p>
          <p className="flex items-center gap-1.5"><i className="ri-building-line text-foreground-300"></i>{resumen.ciudades.length} ciudades</p>
          <p className="flex items-center gap-1.5"><i className="ri-time-line text-foreground-300"></i>{resumen.coberturaHoraria} hrs</p>
          <p className="flex items-center gap-1.5"><i className="ri-price-tag-3-line text-foreground-300"></i>{resumen.categoriasActivas.length} categorías</p>
        </div>
      </div>

      {/* Top rule */}
      <div className="bg-gradient-to-br from-rose-500 to-amber-500 rounded-2xl p-5 text-white">
        <div className="flex items-center gap-2 mb-2">
          <i className="ri-flashlight-line text-xl"></i>
          <h4 className="text-sm font-bold">Regla #1</h4>
        </div>
        <p className="text-xs leading-relaxed opacity-90">{reglas[0].descripcion}</p>
        <Link
          to="/dashboard-simple"
          className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold hover:underline cursor-pointer"
        >
          Gestionar desde Dashboard <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
    </div>
  );
}

/* ─── MODAL EVENTO ─── */
function ModalEvento({ evento, onClose }: { evento: EventoAutomatico; onClose: () => void }) {
  const prog = programas.find((p) => p.id === evento.programaId);
  const radio = radiosConProgramacion.find((r) => r.id === evento.radioId);
  const colorMap: Record<string, string> = {
    emerald: 'from-emerald-500 to-teal-500',
    sky: 'from-sky-500 to-cyan-500',
    amber: 'from-amber-500 to-orange-500',
    violet: 'from-violet-500 to-fuchsia-500',
    rose: 'from-rose-500 to-pink-500',
    orange: 'from-orange-500 to-red-500',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className={`p-5 bg-gradient-to-br ${colorMap[evento.color] ?? 'from-foreground-500 to-foreground-600'} text-white`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <i className={`${evento.icono} text-xl`}></i>
            </div>
            <div>
              <h3 className="text-sm font-bold">{evento.accion}</h3>
              <p className="text-xs opacity-80 capitalize">{evento.tipo.replace(/_/g, ' ')}</p>
            </div>
          </div>
        </div>
        <div className="p-5">
          <p className="text-sm text-foreground-700 leading-relaxed mb-4">{evento.detalle}</p>

          <div className="space-y-2 text-xs">
            <div className="flex justify-between py-1.5 border-b border-background-100">
              <span className="text-foreground-400">Hora de disparo</span>
              <span className="font-semibold text-foreground-900">{evento.horaDisparo.replace('T', ' a las ')}</span>
            </div>
            {prog && (
              <div className="flex justify-between py-1.5 border-b border-background-100">
                <span className="text-foreground-400">Programa</span>
                <span className="font-semibold text-foreground-900">{prog.nombre}</span>
              </div>
            )}
            {radio && (
              <div className="flex justify-between py-1.5 border-b border-background-100">
                <span className="text-foreground-400">Radio</span>
                <span className="font-semibold text-foreground-900">{radio.nombre} {radio.frecuencia} FM</span>
              </div>
            )}
            <div className="flex justify-between py-1.5">
              <span className="text-foreground-400">Estado</span>
              <span className="font-semibold text-emerald-600">Completado</span>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-full mt-5 py-2.5 rounded-xl bg-background-100 text-foreground-700 text-sm font-semibold hover:bg-background-200 transition-colors cursor-pointer"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}